import { useState } from "react";
import { adminRequest, getAdminHeaders } from "./adminApi";

export default function AdminResetPasswordPanel({
  role,
  accountId,
  phone,
  name,
  email,
  emailVerified,
}) {
  const [customPassword, setCustomPassword] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const apiPath =
    role === "consultant"
      ? `/api/admin/consultants/${accountId}/reset-password`
      : `/api/admin/users/${accountId}/reset-password`;

  const handleReset = async () => {
    const label = name || phone || `ID ${accountId}`;
    if (
      !window.confirm(
        `确定为「${label}」重置登录密码吗？\n${
          sendEmail && emailVerified
            ? "系统将尝试发送邮件到客户绑定邮箱。"
            : "客户未绑定邮箱，请复制通知文案通过微信/电话发给客户。"
        }`
      )
    ) {
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const payload = { send_email: sendEmail };
      if (customPassword.trim()) {
        payload.new_password = customPassword.trim();
      }

      const { res, data } = await adminRequest(apiPath, {
        method: "POST",
        headers: getAdminHeaders(true),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        alert(data?.detail || "重置失败");
        return;
      }

      setResult(data);
    } catch (err) {
      console.error(err);
      alert("重置失败");
    } finally {
      setLoading(false);
    }
  };

  const copyNotifyText = async () => {
    if (!result?.notify_text) return;
    try {
      await navigator.clipboard.writeText(result.notify_text);
      alert("通知文案已复制，可粘贴发给客户");
    } catch {
      window.prompt("请复制以下内容发给客户：", result.notify_text);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-gray-100">
      <h3 className="text-lg font-bold text-gray-800 mb-2">重置登录密码</h3>
      <p className="text-xs text-gray-500 mb-4 leading-relaxed">
        适用于客户忘记密码且未绑定邮箱的情况。留空密码将自动生成 8 位临时密码。
      </p>

      <div className="space-y-3 text-sm">
        <div className="bg-gray-50 rounded-xl p-3 text-gray-600">
          <p>手机号：{phone || "-"}</p>
          <p className="mt-1">
            绑定邮箱：
            {emailVerified && email ? email : "未绑定（请复制文案手动发送）"}
          </p>
        </div>

        <input
          type="text"
          value={customPassword}
          onChange={(e) => setCustomPassword(e.target.value)}
          placeholder="自定义新密码（留空则自动生成）"
          className="w-full border rounded-xl px-3 py-2.5"
        />

        <label className="flex items-center gap-2 text-gray-600">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            disabled={!emailVerified || !email}
          />
          发送到客户绑定邮箱
          {!emailVerified || !email ? (
            <span className="text-xs text-amber-600">（未绑定不可用）</span>
          ) : null}
        </label>

        <button
          type="button"
          onClick={handleReset}
          disabled={loading}
          className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold disabled:opacity-50"
        >
          {loading ? "处理中…" : "重置密码并生成通知"}
        </button>

        {result ? (
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 space-y-2">
            <p className="text-green-800 font-medium">密码已重置</p>
            <p className="text-sm text-gray-700">
              新密码：<span className="font-mono font-bold">{result.temp_password}</span>
            </p>
            <p className="text-xs text-gray-500">
              {result.email_sent
                ? `已发送到邮箱 ${result.email_masked}`
                : "未发送邮件，请复制下方文案发给客户"}
            </p>
            <pre className="text-xs whitespace-pre-wrap bg-white rounded-lg p-3 border text-gray-700">
              {result.notify_text}
            </pre>
            <button
              type="button"
              onClick={copyNotifyText}
              className="w-full py-2 rounded-lg bg-white border text-gray-700 hover:bg-gray-50"
            >
              复制通知文案
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
